import { Action, getModule, Module } from 'vuex-module-decorators';

import store from '@/store';
import EntityBaseModule from '@/store/entity';
import ResponseHandlerModule from '@/store/modules/responseHandler';

import { PatientsSubscriptions } from '@/models/patientsSubscriptions';
import { Subscriptions } from '@/models/subscriptions';
import { SubscriptionCard, SubscriptionModel } from '@/types/models/subscriptions';

import { addNewEntity, getById, getList, removeEntity, updateEntity } from '@/api/crud';

import EntityModel from './entityModel';
import { activateSubscription, cancelSubscription, removeSubscription } from '@/api/subscriptions';
import { getApiErrorMessage } from '@/utils/api';
import { InputSuggestionItem } from '@/lib/formFactory/inputSuggestion.interface';

@Module({ dynamic: true, store, name: PatientsSubscriptions.moduleEntityName, namespaced: true })
class SubscriptionEntityModule extends EntityBaseModule {
    model: SubscriptionModel;

    constructor(module: SubscriptionEntityModule) {
        super(module);

        const entityModel = new EntityModel();
        this.model = entityModel.model;
    }

    @Action({ rawError: true })
    async getById(id: string): Promise<SubscriptionCard> {
        try {
            const result = await getById(Subscriptions.moduleName, id);

            return result[0];
        } catch (clientError) {
            const error = clientError.response.data;

            throw new Error(error.message);
        }
    }

    @Action({ rawError: true })
    async createNew(data: Record<string, any>) {
        try {
            const { result } = await addNewEntity(Subscriptions.moduleName, data);
            return result.id;
        } catch (error) {
            if (error.response?.data) {
                ResponseHandlerModule.showNotify({ message: error.response.data, type: 'fail' });

                return;
            }
            ResponseHandlerModule.showNotify({ message: error, type: 'fail' });
        }
    }

    @Action({ rawError: true })
    async update(params: { id: string; data: Record<string, any> }) {
        try {
            return updateEntity(Subscriptions.moduleName, params.id, params.data);
        } catch (error) {
            if (error.response?.data) {
                ResponseHandlerModule.showNotify({ message: error.response.data, type: 'fail' });
                return;
            }
            ResponseHandlerModule.showNotify({ message: error, type: 'fail' });
        }
    }

    @Action({ rawError: true })
    async remove(id: string) {
        try {
            return removeEntity(Subscriptions.moduleName, id)
        } catch (error) {
            if (error.response?.data) {
                ResponseHandlerModule.showNotify({ message: error.response.data, type: 'fail' });
                return;
            }
            ResponseHandlerModule.showNotify({ message: error, type: 'fail' });
        }
    }

    @Action({ rawError: true })
    async sendActivateSubscription(id: string) {
        try {
            const result = await activateSubscription(id);
            return result.status;
        } catch (error) {
            const message = getApiErrorMessage(error as any);
            ResponseHandlerModule.showNotify({ message, type: 'fail' });
        }
    }

    @Action({ rawError: true })
    async sendCancelSubscription(id: string) {
        try {
            const result = await cancelSubscription(id);   
            return result.status;
        } catch (error) {
            const message = getApiErrorMessage(error as any);
            ResponseHandlerModule.showNotify({ message, type: 'fail' });
        }
    }

    @Action({ rawError: true })
    async sendRemoveSubscription(id: string) {
        try {
            const result = await removeSubscription(id);
            return result.status;
        } catch (error) {
            const message = getApiErrorMessage(error as any);
            ResponseHandlerModule.showNotify({ message, type: 'fail' });
        }
    }

    @Action({ rawError: true })
    async searchSubscriptionType(search: string) {
        this.SET_SUGGESTIONS_VALUE_BY_TYPE({ type: 'subscriptionType', value: search });

        const result = await getList(
            'subscription-types',
            { page: 1, items: 50 },
            '',
            `filters[0][id]=title&filters[0][value]=${search}`);
        
        const list = result.table.rows.map((row: Record<string, any>) => {
            return { id: row.id, value: row.title };
        });

        this.SET_SUGGESTIONS_HINTS_LIST_BY_TYPE({ type: 'subscriptionType', list });
    }

    @Action({ rawError: true })
    updateSubscriptionType(current: InputSuggestionItem) {
        this.updateSuggestionsCurrentByType({ type: 'subscriptionType', current });
    }
}

export default getModule(SubscriptionEntityModule);
